import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Church, ArrowLeft, Check, Music4, Users, Piano, Mic2, Disc3 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const GOSPEL = ["Cece Winans", "Richard Smallwood", "Donnie McClurkin", "Micah Stampley", "Myron Butler", "Jonathan Nelson", "Kurt Carr", "Ann Nesby", "Ruby Turner"];

const OFFERINGS = [
  { icon: Piano, title: "Musical Direction", blurb: "MD-ing your services and events — leading the band, shaping arrangements and holding the musical flow of worship." },
  { icon: Mic2, title: "Playing for worship", blurb: "Sensitive, spirit-led piano and keys for services, conferences and gospel concerts." },
  { icon: Users, title: "Training your worship team", blurb: "Coaching worship musicians and church bands — reading, playing by ear, improvising and playing together tightly." },
  { icon: Disc3, title: "Arrangements & tracks", blurb: "Bespoke arrangements and backing tracks in your key and style for your worship team." },
];

const INTERESTS = ["Musical Direction", "Playing for worship", "Training our worship team", "Arrangements / backing tracks", "Something else"];

export default function WorshipServices() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [church, setChurch] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [interest, setInterest] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const composed = `Worship / gospel enquiry\nChurch / organisation: ${church || "—"}\nInterested in: ${interest || "—"}\n\n${message}`;
    try {
      await api.post("/contact", { name, email, message: composed });
      setDone(true);
      toast.success("Thanks — your enquiry is on its way to Jason");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 sm:px-8 py-12 lg:py-16">
        <Link to="/" data-testid="worship-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft size={15} /> Back to the hub
        </Link>

        {/* Hero */}
        <div className="mt-6 flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Church size={24} /></span>
          <span className="rounded-full bg-secondary px-3 py-1 text-[11px] uppercase tracking-widest text-muted-foreground">Churches & worship teams</span>
        </div>
        <div className="mt-5 grid items-center gap-8 md:grid-cols-[1fr_300px]">
        <div>
        <h1 className="font-serif text-4xl tracking-tight sm:text-5xl">Gospel & worship music, done right</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Much of my heart — and my career — is in the church. I understand exactly how a worship moment needs to
          breathe, and how a church band needs to read, feel and support it. Whether you need a Musical Director, a
          player for a service or concert, or someone to raise the level of your whole worship team, I'd love to help.
        </p>
        </div>
        <div className="overflow-hidden rounded-3xl border border-border shadow-2xl shadow-black/30">
          <img src="/media/jt-worship-nord.jpg" alt="Jason Thompson playing keys in church" className="aspect-[4/5] w-full object-cover object-top" data-testid="worship-hero-image" />
        </div>
        </div>

        {/* Gospel credits */}
        <div className="mt-8 rounded-2xl border border-border bg-card p-6" data-testid="worship-credits">
          <p className="font-mono text-[11px] uppercase tracking-widest text-primary">Played for & shared the stage with</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {GOSPEL.map((a) => (
              <span key={a} className="rounded-full border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground">{a}</span>
            ))}
            <span className="rounded-full bg-primary/10 px-3 py-1.5 text-sm text-primary">& many more</span>
          </div>
        </div>

        {/* Offerings */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="worship-offerings">
          {OFFERINGS.map((o) => (
            <div key={o.title} className="rounded-2xl border border-border bg-card p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><o.icon size={20} /></span>
              <h3 className="mt-4 font-serif text-lg">{o.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{o.blurb}</p>
            </div>
          ))}
        </div>

        {/* Enquiry form */}
        <div className="mt-10 rounded-3xl border border-border bg-card p-6 sm:p-8" id="enquire" data-testid="worship-enquiry">
          <div className="flex items-center gap-2"><Music4 size={18} className="text-primary" /><h2 className="font-serif text-2xl">Tell me about your church</h2></div>
          {done ? (
            <div className="mt-6 rounded-2xl border border-primary/40 bg-primary/10 p-8 text-center" data-testid="worship-success">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground"><Check size={26} /></span>
              <h3 className="mt-5 font-serif text-2xl">Thank you, {name.split(" ")[0]}!</h3>
              <p className="mt-3 text-muted-foreground">Your enquiry is on its way to Jason — I'll be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-5" data-testid="worship-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="w-name" className="text-muted-foreground">Your name</Label>
                  <Input id="w-name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="worship-name" placeholder="Your name" className="mt-1 border-border bg-background" />
                </div>
                <div>
                  <Label htmlFor="w-church" className="text-muted-foreground">Church / organisation</Label>
                  <Input id="w-church" value={church} onChange={(e) => setChurch(e.target.value)} data-testid="worship-church" placeholder="e.g. Grace Community Church" className="mt-1 border-border bg-background" />
                </div>
              </div>
              <div>
                <Label htmlFor="w-email" className="text-muted-foreground">Email</Label>
                <Input id="w-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="worship-email" placeholder="you@example.com" className="mt-1 border-border bg-background" />
              </div>
              <div>
                <Label className="text-muted-foreground">What do you need?</Label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="worship-interests">
                  {INTERESTS.map((it) => (
                    <button
                      key={it} type="button" onClick={() => setInterest(it)}
                      data-testid={`worship-interest-${it.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${interest === it ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground hover:border-primary"}`}
                    >
                      {it}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="w-msg" className="text-muted-foreground">Tell me more</Label>
                <Textarea id="w-msg" required value={message} onChange={(e) => setMessage(e.target.value)} data-testid="worship-message" placeholder="Dates, what you're planning, the size of your team…" rows={4} className="mt-1 border-border bg-background" />
              </div>
              <Button type="submit" disabled={loading} data-testid="worship-submit" className="w-full rounded-full bg-primary py-6 text-base text-foreground hover:bg-primary/90">
                {loading ? "Sending…" : "Send enquiry"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
